import React from 'react';
import { Search } from 'lucide-react';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Sizing and layout for the wrapper — width, margins. */
  className?: string;
  /** Escape hatch for the field itself, when a surface needs a different height. */
  inputClassName?: string;
  maxLength?: number;
}

/** Longest search a field accepts. Matches the backend's own cap on `search`. */
const DEFAULT_MAX_LENGTH = 120;

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  inputClassName,
  maxLength = DEFAULT_MAX_LENGTH,
}) => {
  // Sprint 28, story-142. Decision 2 (docs/architecture.md "## Sprint 28") — own default via
  // `common.*`; a caller with entity-specific placeholder copy still overrides via the prop.
  const tCommon = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? `${tCommon.search}...`;
  return (
    // No default width: callers size this, and a hardcoded w-64 here fought every
    // width they passed, leaving two conflicting classes on the element.
    <div className={cn('relative', className)}>
      <Search
        size={17}
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
      />
      <input
        type="text"
        placeholder={resolvedPlaceholder}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          // pl-10 clears the icon with room to spare — at pl-9 the caret sat
          // almost against it. py-2.5 gives the field the same optical weight as
          // the buttons it sits beside.
          'w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-3 text-sm',
          'placeholder:text-gray-400',
          'focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500',
          'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100',
          inputClassName,
        )}
      />
    </div>
  );
};

export default SearchInput;
